import { useStageMix } from "../hooks/use-intelligence-queries.ts";
import { exportCsv } from "../lib/csv-export.ts";

function formatDollars(n: number | null): string {
  if (n == null) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export function StageMix() {
  const { data, isLoading, isError } = useStageMix();

  if (isLoading && !data) return <div className="intel-loading">Loading stage mix…</div>;
  if (isError && !data) return <div className="intel-error">Failed to load stage mix</div>;
  if (!data) return null;

  const handleExport = () => {
    exportCsv(
      "intelligence-stage-mix.csv",
      ["Stage", "Rounds", "Median raise (USD)", "Median score"],
      data.map((r) => [
        r.stage,
        r.roundCount,
        r.medianRaiseUsd ?? "",
        r.medianScore != null ? r.medianScore.toFixed(1) : "",
      ])
    );
  };

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <div className="intel-section__header-row">
          <div>
            <h3 className="intel-section__title">Stage mix</h3>
            <p className="intel-section__subtitle">
              Round count, median raise, and median project score by canonical funding stage
              (Pre-Seed / Seed / Strategic / Series A+). "Median raise" is at the round level — each
              funding round contributes once. "Median score" is at the project level — a project
              that raised at a given stage contributes its current alpha score to that stage's
              median. Stage labels are normalized from the raw RootData / DefiLlama values; noise
              labels ("Unknown", "--") are excluded.
            </p>
          </div>
          {data.length > 0 && (
            <button
              type="button"
              className="intel-csv-btn"
              onClick={handleExport}
              aria-label="Download stage mix as CSV"
            >
              Download CSV
            </button>
          )}
        </div>
      </header>

      {data.length === 0 ? (
        <div className="intel-coinv-empty">No canonical-stage rounds available.</div>
      ) : (
        <div className="intel-stage-wrap">
          <table className="intel-stage">
            <thead>
              <tr>
                <th>Stage</th>
                <th className="intel-stage__num-head">Rounds</th>
                <th className="intel-stage__num-head">Median raise</th>
                <th className="intel-stage__num-head">Median score</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.stage}>
                  <td className="intel-stage__name">{row.stage}</td>
                  <td className="intel-stage__num">{row.roundCount}</td>
                  <td className="intel-stage__num">{formatDollars(row.medianRaiseUsd)}</td>
                  <td className="intel-stage__num">
                    {row.medianScore != null ? row.medianScore.toFixed(1) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
